import React from 'react';
import { Ear, Waves, Gauge, Crosshair } from 'lucide-react';

const POINTS = [
  {
    icon: Ear,
    title: 'A quieter headset baseline',
    text: 'At appropriate sensitivity settings an AMT hears very little when no meaningful ultrasonic source is present — so a small leak, discharge or bypass emerges distinctly instead of being layered over electronic chatter.',
  },
  {
    icon: Waves,
    title: 'High usable signal-to-noise',
    text: 'What matters in troubleshooting is how clearly a weak fault signal stands out from the instrument\'s own audible background. The UL101 is designed around that practical distinction, not raw gain.',
  },
  {
    icon: Gauge,
    title: 'Fixed around 40 kHz — by design',
    text: 'The UL101 listens in a tightly controlled band centered near 40 kHz, the region most useful for turbulent leaks and electrical discharge. No hunting for a listening frequency: adjust sensitivity and close in.',
  },
  {
    icon: Crosshair,
    title: 'Scan, hear a change, pinpoint',
    text: 'Progressive sensitivity reduction and probe directionality let a technician separate one source from nearby components — fast, and often without disassembly just to begin the search.',
  },
];

export const UL101Technology = () => (
  <section className="py-16 sm:py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white" data-testid="ul101-technology">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
        <div className="lg:col-span-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-400 mb-3">Technology</p>
          <h2 className="text-3xl sm:text-4xl font-bold leading-tight">Why the UL101 Offers Superior Sound Quality</h2>
          <p className="mt-5 text-blue-100 leading-relaxed">
            The UL101 uses a <strong className="text-white">proprietary non-heterodyne method</strong> to convert ultrasonic activity around 40 kHz into an audible signal. Unlike a typical heterodyne instrument, it does not rely on an internally generated mixing signal to create the sound the technician hears.
          </p>
          <p className="mt-4 text-blue-100 leading-relaxed">
            The practical result is an exceptionally quiet baseline, high usable signal-to-noise and a simple "point, listen and pinpoint" workflow — especially well suited to aviation maintenance troubleshooting. The UL101's non-heterodyne architecture reflects a specific design priority: helping AMTs locate weak, localized ultrasonic sources quickly and clearly.
          </p>
        </div>
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
          {POINTS.map(({ icon: Icon, title, text }) => (
            <div key={title} className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-5 sm:p-6 hover:bg-white/10 transition-colors" data-testid="ul101-technology-point">
              <div className="h-10 w-10 rounded-xl bg-orange-500/20 text-orange-300 flex items-center justify-center mb-4">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="text-base font-bold text-white mb-2">{title}</h3>
              <p className="text-sm text-blue-100 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);
